class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.predictiveSearchResults = this.querySelector(
      '[data-predictive-search]'
    );
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener(
      'submit',
      this.onFormSubmit.bind(this)
    );

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.abortController.abort();
      this.predictiveSearchResults.innerHTML = '';
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.predictiveSearchResults.innerHTML = '';
      this.close(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
      return;
    }

    // Always request fresh results when the input receives focus.
    this.getSearchResults(currentSearchTerm);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.abortController.abort();
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.abortController.abort();
      this.predictiveSearchResults.innerHTML = '';
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      '[data-predictive-search-search-for-text]'
    );
    const currentButtonText = searchForTextElement?.innerText;

    if (!currentButtonText || !previousTerm) return;

    const occurrences = currentButtonText.split(previousTerm).length - 1;

    if (occurrences !== 1) return;

    searchForTextElement.innerText = currentButtonText.replace(
      previousTerm,
      newTerm
    );
  }

  switchOption(direction) {
    if (!this.hasAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector(
      '[aria-selected="true"]'
    );

    const allVisibleElements = Array.from(
      this.querySelectorAll('li, button.predictive-search__item')
    ).filter((element) => element.offsetParent !== null);

    if (!allVisibleElements.length) return;
    if (moveUp && !selectedElement) return;

    const selectedElementIndex =
      allVisibleElements.indexOf(selectedElement);

    let activeElementIndex = 0;

    if (selectedElementIndex !== -1) {
      if (moveUp) {
        activeElementIndex =
          selectedElementIndex === 0
            ? allVisibleElements.length - 1
            : selectedElementIndex - 1;
      } else {
        activeElementIndex =
          selectedElementIndex === allVisibleElements.length - 1
            ? 0
            : selectedElementIndex + 1;
      }
    }

    if (activeElementIndex === selectedElementIndex) return;

    if (this.statusElement) {
      this.statusElement.textContent = '';
    }

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', 'true');

    if (selectedElement) {
      selectedElement.setAttribute('aria-selected', 'false');
    }

    this.input.setAttribute(
      'aria-activedescendant',
      activeElement.id
    );
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    this.abortController.abort();
    this.abortController = new AbortController();

    const controller = this.abortController;
    const requestedTerm = searchTerm.trim();

    this.closeResults();
    this.predictiveSearchResults.innerHTML = '';
    this.removeAttribute('results');
    this.setLiveRegionLoadingState();

    const params = new URLSearchParams({
      q: requestedTerm,
      section_id: 'predictive-search',
      'resources[options][fields]':
        'title,variants.sku,variants.title',
    });

    const isCurrentRequest = () =>
      !controller.signal.aborted &&
      this.abortController === controller &&
      this.getQuery() === requestedTerm;

    fetch(`${routes.predictive_search_url}?${params.toString()}`, {
      signal: controller.signal,
      cache: 'no-store',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Predictive search: ${response.status}`
          );
        }

        return response.text();
      })
      .then((text) => {
        if (!isCurrentRequest()) return;

        const parsedDocument = new DOMParser().parseFromString(
          text,
          'text/html'
        );

        const section = parsedDocument.querySelector(
          '#shopify-section-predictive-search'
        );

        if (!section) {
          throw new Error(
            'Predictive search section not found'
          );
        }

        this.renderSearchResults(section.innerHTML);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        if (!isCurrentRequest()) return;

        this.removeAttribute('results');
        this.close();
        console.error(error);
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector('.predictive-search-status');

    this.loadingText =
      this.loadingText ||
      this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText || '');
    this.setAttribute('loading', 'true');
  }

  setLiveRegionText(statusText) {
    this.statusElement =
      this.statusElement ||
      this.querySelector('.predictive-search-status');

    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    clearTimeout(this.liveRegionTimeout);

    this.liveRegionTimeout = setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', 'true');

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const countElement = this.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );

    this.setLiveRegionText(countElement?.textContent || '');
  }

  getResultsMaxHeight() {
    const headerBottom =
      document
        .querySelector('.section-header')
        ?.getBoundingClientRect().bottom || 0;

    this.resultsMaxHeight = Math.max(
      0,
      window.innerHeight - headerBottom
    );

    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      `${this.getResultsMaxHeight()}px`;

    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector(
      '[aria-selected="true"]'
    );

    if (selected) {
      selected.setAttribute('aria-selected', 'false');
    }

    this.input.setAttribute('aria-activedescendant', '');
    this.input.setAttribute('aria-expanded', 'false');

    this.removeAttribute('loading');
    this.removeAttribute('open');

    this.resultsMaxHeight = false;
    this.isOpen = false;

    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
